import { ReactElement, useEffect, useState } from "react";
import RadarChart from "../../../../../components/charts/radarChart";
import { IParentSize, IRealtimeData } from "../../../../../types";
import { IDashboardComponent, IRadarConfig, IRadarLimit, IRadarVariable } from "../../../types";
import {
  IAxisBand,
  IAxisLinear,
  IPoint as CommonPoint,
  IRadarChartConfig, SeverityEnum
} from "../../../../../components/charts/types";
import { getPalette } from "../../../../../configuration/configuration";
import { getFloatValue } from "../../../../../system/utils";

interface IProps {
  parentSize: IParentSize;
  realtime: Record<string, IRealtimeData>;
  component: IDashboardComponent<IRadarConfig>;
}



interface IRadarPoint extends CommonPoint {
  data: IRadarVariable;
}

const getSeverityValue = (severity: "error" | "warning" | "info" | null) => {
  switch (severity) {
    case "error":
      return SeverityEnum.error;
    case "warning":
      return SeverityEnum.warning;
    case "info":
      return SeverityEnum.info;
    default:
      return SeverityEnum.none;
  }
};

const getSeverityText = (severity: SeverityEnum): "error" | "warning" | "info" | "" => {
  switch (severity) {
    case SeverityEnum.error:
      return  "error";
    case SeverityEnum.warning:
      return "warning";
    case SeverityEnum.info:
      return "info";
    default:
      return "";
  }
};

const getStatus = (config: IRadarVariable, value: number, realtime: Record<string, IRealtimeData>): "OK" | "BAD"  | "UP"  | "DOWN"  | "NONE" => {
  if (config.limits.aim.type === "VALUE" && typeof config.limits.aim.value === "number" ) {
    if (value > config.limits.aim.value) {
      return "UP";
    }

    return value < config.limits.aim.value ? "DOWN": "OK";
  }

  if (config.limits.aim.type === "TAG" && config.limits.aim.value !== "") {
    const tagValue = realtime[config.limits.aim.value];

    if(!tagValue || typeof tagValue.Value !== "number") {
      return "BAD";
    }

    if (value < tagValue.Value) {
      return "DOWN";
    }

    return value > tagValue.Value ? "UP" : "OK";
  }

  return "NONE";
};

const getLimitValue = (limit: IRadarLimit, realtime: Record<string, IRealtimeData>, value: number | null): number | null => {
  if (limit.type === "VALUE") {
    return limit.value as number;
  }

  if (limit.type === "TAG") {
    return realtime[limit.value] ? getFloatValue(realtime[limit.value], 0) : null;
  }

  return value;
};

const evalLimitSeverity = (limit: IRadarLimit, value: number, realtime: Record<string, IRealtimeData>): SeverityEnum => {
  if (limit.severity) {
    if (limit.type === "VALUE") {
      const lValue = limit.value as number;

      return limit.comparison === ">" && value > lValue || limit.comparison === "<" && value < lValue
        ? getSeverityValue(limit.severity)
        : SeverityEnum.none;
    }

    if (limit.type === "TAG") {
      const lValue = realtime && realtime[limit.value] ? realtime[limit.value].Value : null;

      if (typeof lValue === "number") {
        return  lValue && (limit.comparison === ">" && value > lValue || limit.comparison === "<" && value < lValue)
          ? getSeverityValue(limit.severity)
          : SeverityEnum.none;
      }
    }
  }

  return SeverityEnum.none;
};

const RadarWidget = ({ component, realtime, parentSize }: IProps): ReactElement | null => {
  const [ radarChartConfig, setRadarChartConfig ] = useState<IRadarChartConfig | null>(null);
  const [ errorClass, setErrorClass ] = useState<"error" | "warning" | "info" | "">("");

  useEffect(() => {
    const titles: Record<string, string> = {};
    let errorCls: SeverityEnum = SeverityEnum.none;

    const items: IRadarPoint[] = component.config.variables.map(d => {
      const value = realtime ? getFloatValue(realtime[d.code], 100) : 100;
      let severity = SeverityEnum.none;
      titles[d.hash] = d.title;

      if (!d.limits.maximum.comparison) {
        // TEMP: compatibility
        d.limits.maximum.comparison = ">";
      } else {
        const s1 = evalLimitSeverity(d.limits.maximum, value, realtime) ?? "";

        errorCls = s1 > errorCls ? s1 : errorCls;
        severity = s1 > severity ? s1 : severity;
      }

      if (!d.limits.minimum.comparison) {
        // TEMP: compatibility
        d.limits.minimum.comparison = "<";
      } else if (errorCls !== SeverityEnum.error){
        const s2 = evalLimitSeverity(d.limits.minimum, value, realtime);

        errorCls = s2 > errorCls ? s2 : errorCls;
        severity = s2 > severity ? s2 : severity;
      }

      return { x: d.hash, y: value, data: d, status: getStatus(d, value, realtime), color: d.color, severity };
    });

    setErrorClass(getSeverityText(errorCls));

    const yAxis: IAxisLinear = {
      name: "yAxis",
      minimum: 0,
      maximum: 100,
      hiddenTickText: true,
      ticksCount: 4,
      formatTick: n => (n as number).toFixed(0)
    };

    const xAxis: IAxisBand = {
      name: "xAxis",
      categories: component.config.variables.map(o => o.hash),
      formatTick: (hash) => titles[hash as string]
    };

    setRadarChartConfig({
      width: parentSize.width * (component.config.scale ?? 1),
      height: parentSize.height * (component.config.scale ?? 1) - 15,
      showLegend: false,
      series: [{
        name: "Variables",
        xAxisName: "xAxis",
        yAxisName: "yAxis",
        value: items,
        converter: (point) => {
          const radarPoint = point as IRadarPoint;
          const limits = radarPoint.data.limits;
          const aim = getLimitValue(limits.aim, realtime, null);
          const maximum = getLimitValue(limits.maximum, realtime, radarPoint.data.maximum) ?? 100;
          const minimum = getLimitValue(limits.minimum, realtime, radarPoint.data.minimum) ?? 0;

          if (aim) {
            if (radarPoint.y <= aim) {
              const lenAimMin = aim - minimum;
              const factorAimMin = 100 / (lenAimMin !== 0 ? lenAimMin : 1);

              return Math.min((aim - radarPoint.y) * factorAimMin, 100);
            }

            const lenAimMax = maximum - aim;
            const factorAimMax = 100 / (lenAimMax !== 0 ? lenAimMax : 1);

            return Math.min((radarPoint.y - aim) * factorAimMax, 100);
          }

          const len = maximum - minimum;
          const factor = len !== 0 ? 100 / len : 1;

          return (radarPoint.y - minimum) * factor;
        },
        color: getPalette().list[5],
      }],
      yAxis,
      xAxis
    });
  }, [ component, realtime, parentSize ]);

  return radarChartConfig
    ? <div className={`display-grid text-center align-content-center h-100 ${errorClass !== "" ? `${errorClass}-status` : ""}`} >
      <RadarChart radarConfig={radarChartConfig} selectedItem={null} size={10 * (component.config.scale ?? 1)}/>
    </div>
    : null;
};

export default RadarWidget;
